import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FormFeedback, Input } from 'reactstrap';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useGetVideosListQuery } from '../../../../../service';
import playlistApi from '../../../../../api/playlistApi';
import { getVideoDropDownList } from '../../../../../redux/videoSlice';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '../../../../../data/atoms/utilsAtoms';
import Toast from '../../../../../utils/toastManager';

const DUPLICATE_PLAYLIST_MESSAGE = 'Unable to add this video because it is already in this playlist.';

const getVideoId = video => video?._id?.toString?.() || video?.value?.toString?.() || video?.id?.toString?.();

function PersonalPlayListForm({ show, setShow, editPlaylistID, setnModalShow }) {
	// Form setup
	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors }
	} = useForm();

	// Hooks
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Redux state
	const userData = useAtomValue(userInfoAtom);
	const videoDropdownList = useSelector(state => state.videoState.videoDropdownList);

	// Local state
	const [name, setName] = useState('');
	const [videos, setVideos] = useState([]);
	const [existingVideos, setExistingVideos] = useState([]);
	const [loading, setLoading] = useState(false);

	// Fetch videos query
	const {
		data: videoData,
		isLoading: videosLoading,
		isSuccess: videosSuccess,
		error: videosError
	} = useGetVideosListQuery();

	// Process video data for dropdown
	useEffect(() => {
		if (videosLoading) {
			setLoading(true);
		}

		if (videosError) {
			console.error('Error fetching videos:', videosError);
		}

		if (videosSuccess && videoData) {
			const formattedVideos = videoData.map(video => ({
				...video,
				label: video.title,
				value: video._id
			}));

			dispatch(getVideoDropDownList(formattedVideos));
			setLoading(false);
		}
	}, [videoData, videosLoading, videosSuccess, videosError, dispatch]);

	// Fetch playlist data if editing
	useEffect(() => {
		if (!editPlaylistID) return;

		setLoading(true);

		const fetchPlaylistDetails = async () => {
			try {
				const response = await axios.get(`${import.meta.env.VITE_APP_API_URL}/personalvideo/${editPlaylistID}`);

				const playlistData = response.data.existingVideos;

				// Format videos for the select component
				const formattedVideos = playlistData.videos.map(video => ({
					label: video.title,
					value: video._id,
					...video
				}));

				// Update state
				setName(playlistData.name);
				setExistingVideos(formattedVideos);
				setVideos(playlistData.videos);

				// Update form values
				reset({
					createdAt: playlistData.createdAt,
					description: playlistData.description,
					name: playlistData.name,
					videos: formattedVideos,
					id: playlistData._id,
					userId: userData.user?.userId
				});
			} catch (error) {
				console.error('Error fetching playlist details:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchPlaylistDetails();
	}, [editPlaylistID, reset, userData.user?.toString()]);

	// Form submission handler
	const onSubmit = async formData => {
		try {
			setLoading(true);
			const selectedVideoIds = (formData.videos || []).map(getVideoId).filter(Boolean);
			if (new Set(selectedVideoIds).size !== selectedVideoIds.length) {
				Toast.error(DUPLICATE_PLAYLIST_MESSAGE);
				return;
			}

			const payload = {
				name: name,
				id: editPlaylistID,
				videos: formData.videos,
				userId: userData.user?.userId
			};

			const response = await playlistApi.saveNewPlaylist(payload, userData?.token);

			if (response) {
				// Update state with new playlist data
				setExistingVideos(formData.videos);
				setVideos(formData.videos);

				// Close modals
				setShow(false);
				if (setnModalShow) setnModalShow(false);
			}
		} catch (error) {
			console.error('Error saving playlist:', error);
			Toast.error(error?.response?.data?.message || 'Error saving playlist');
		} finally {
			setLoading(false);
		}
	};

	return (
		<section>
			<div className="container">
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="row g-2">
						{/* Playlist Name */}
						<div className="col-md-12 form-group">
							<Input
								type="text"
								placeholder="Name"
								value={name}
								onChange={e => {
									setName(e.target.value);
									setValue('name', e.target.value);
								}}
								invalid={!!errors.name}
							/>
							{errors.name && <FormFeedback>{errors.name.message}</FormFeedback>}
						</div>

						{/* Video Selection */}
						<div className="col-md-12 form-group">
							<Controller
								name="videos"
								control={control}
								rules={{
									required: 'At least one video must be selected'
								}}
								render={({ field }) => (
									<Select
										{...field}
										isMulti
										options={videoDropdownList || []}
										value={existingVideos || []}
										onChange={selected => {
											setVideos(selected);
											setExistingVideos(selected);
											field.onChange(selected);
										}}
										placeholder="Select videos"
										isDisabled={loading}
									/>
								)}
							/>
							{errors.videos && <FormFeedback style={{ display: 'block' }}>{errors.videos.message}</FormFeedback>}
						</div>

						{/* Submit Button */}
						<div className="col-md-12 form-group action__btns text-end">
							<button className="btn btn-primary" type="submit" disabled={loading}>
								{loading ? 'Processing...' : editPlaylistID ? 'Update' : 'Save'}
							</button>
						</div>
					</div>
				</form>
			</div>
		</section>
	);
}

export default PersonalPlayListForm;
